import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { RiskService } from "@gloria-public/risk/domain/services/risk.service";
import { Subject, debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil } from "rxjs";

@Component({
  selector: "app-search-risk-tags",
  templateUrl: "./search-risk-tags.component.html",
  styleUrls: ["./search-risk-tags.component.scss"],
})
export class SearchRiskTagsComponent implements OnInit, OnDestroy {
  @Input() selectedTags: string[] = [];

  @Output() selectedTagsChange = new EventEmitter<string[]>();

  public tagSearchControl = new FormControl("");
  public availableTags: string[] = [];
  public showSuggestions = false;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private riskService: RiskService,
    private elementRef: ElementRef
  ) {}

  /**
   * Loads and updates available risk tags based on the current search input.
   */
  ngOnInit(): void {
    this.tagSearchControl.valueChanges
      .pipe(
        startWith(""),
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((value) => this.riskService.getRiskTags(value ?? "", this.selectedTags)),
        takeUntil(this.onDestroy$)
      )
      .subscribe((tags) => {
        this.availableTags = tags;
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  @HostListener("document:click", ["$event"])
  public onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);

    if (!clickedInside) {
      this.showSuggestions = false;
    }
  }

  public selectTag(tag: string): void {
    if (this.selectedTags.includes(tag)) {
      return;
    }

    this.selectedTagsChange.emit([...this.selectedTags, tag]);

    this.tagSearchControl.setValue("");
    this.showSuggestions = false;
  }

  public removeTag(tag: string): void {
    this.selectedTagsChange.emit(this.selectedTags.filter((t) => t !== tag));
  }
}
